import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, AlertCircle, ArrowLeft, CheckCircle, LayoutDashboard, FileText, Users, KanbanSquare, Bell, Receipt, Shield, Zap, RefreshCw } from 'lucide-react';
import { authAPI, captchaAPI } from '../../lib/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  { icon: FileText, title: 'Конструктор форм', desc: 'Создавайте уникальные формы заказов под свой бизнес' },
  { icon: LayoutDashboard, title: 'Заказы и финансы', desc: 'Статусы, предоплата, авто-транзакции и аналитика доходов' },
  { icon: Receipt, title: 'Счета и оплата', desc: 'Выставляйте счета клиентам с публичной ссылкой для оплаты' },
  { icon: Users, title: 'База клиентов', desc: 'Контакты, соцсети и полная история заказов каждого клиента' },
  { icon: KanbanSquare, title: 'Kanban-доска задач', desc: 'Приоритеты, дедлайны, привязка к заказам, счетам и материалам' },
  { icon: Bell, title: 'Уведомления', desc: 'Telegram-бот, email и push-уведомления о событиях' },
];

function AuthLeftPanel() {
  return (
    <div 
      className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
      style={{ backgroundImage: 'url(/auth-bg.jpg)' }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-slate-900/75 to-orange-900/60" />
      <div className="relative z-10 p-10 xl:p-12 flex flex-col justify-between w-full">
        {/* Logo */}
        <div className="-mt-2">
          <img src="/logo-dark.svg" alt="YaDela" className="h-16 xl:h-20 w-auto" />
        </div>

        {/* Features */}
        <div className="flex-1 flex flex-col justify-center mt-1">
          <h2 className="text-white text-2xl xl:text-[28px] font-bold leading-tight mb-2">
            CRM-система для мастеров<br />и малого бизнеса
          </h2>
          <p className="text-white/50 text-sm mb-7">
            Всё, что нужно для управления вашим делом — в одном месте
          </p>

          <div className="grid grid-cols-1 gap-3">
            {features.map((f, i) => (
              <div
                key={i}
                className="flex items-start gap-3.5 rounded-xl bg-white/[0.07] backdrop-blur-sm border border-white/[0.08] px-4 py-3 transition-colors hover:bg-white/[0.11]"
              >
                <div className="shrink-0 w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center mt-0.5">
                  <f.icon className="h-[18px] w-[18px] text-orange-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold leading-snug">{f.title}</p>
                  <p className="text-white/45 text-xs leading-snug mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="flex items-center gap-3 pt-4">
          <div className="flex items-center gap-1.5">
            <Shield className="h-4 w-4 text-emerald-400/80" />
            <span className="text-white/50 text-xs">Безопасно</span>
          </div>
          <span className="text-white/20">·</span>
          <div className="flex items-center gap-1.5">
            <Zap className="h-4 w-4 text-amber-400/80" />
            <span className="text-white/50 text-xs">Быстро</span>
          </div>
          <span className="text-white/20">·</span>
          <span className="text-white/50 text-xs">Бесплатный старт</span>
        </div>
      </div>
    </div>
  );
}

// Map backend error messages to Russian
function getErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (!detail) return null;
  if (typeof detail === 'string') {
    const lower = detail.toLowerCase();
    if (lower.includes('invalid credentials') || lower.includes('incorrect')) {
      return 'Неверный email или пароль';
    }
    if (lower.includes('already registered') || lower.includes('already exists') || lower.includes('уже зарегистрирован')) {
      return 'Пользователь с таким email уже зарегистрирован';
    }
    if (lower.includes('not found')) {
      return 'Пользователь не найден';
    }
    if (lower.includes('password')) {
      return 'Неверный пароль';
    }
    if (lower.includes('соцсеть') || lower.includes('oauth')) {
      return detail;
    }
    if (lower.includes('слишком много')) {
      return detail;
    }
    return detail;
  }
  return 'Произошла ошибка';
}

function AuthContent({ 
  activeTab, 
  setActiveTab, 
  loginEmail, 
  setLoginEmail, 
  loginPassword, 
  setLoginPassword,
  loginError,
  setLoginError,
  registerName,
  setRegisterName,
  registerEmail,
  setRegisterEmail,
  registerPassword,
  setRegisterPassword,
  registerConfirmPassword,
  setRegisterConfirmPassword,
  registerError,
  agreeTerms,
  setAgreeTerms,
  showTerms,
  setShowTerms,
  loading,
  handleLogin,
  handleRegister,
  handleGoogleLogin,
  googleLoading,
  handleYandexLogin,
  yandexLoading,
  handleVkLogin,
  vkLoading,
  showForgotPassword,
  setShowForgotPassword,
  forgotEmail,
  setForgotEmail,
  forgotLoading,
  forgotSent,
  handleForgotPassword,
  captchaEnabled,
  captchaQuestion,
  captchaAnswer,
  setCaptchaAnswer,
  captchaLoading,
  loadCaptcha
}) {
  // Forgot password view
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex" data-testid="auth-page">
        {/* Left side - Features */}
        <AuthLeftPanel />

        {/* Right side - Forgot password form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-background">
          <Card className="w-full max-w-md border-border shadow-sm">
            <CardHeader className="text-center pb-2">
              <div className="lg:hidden mb-3 mt-2">
                <img 
                  src="/logo-light.svg" 
                  alt="YaDela" 
                  className="h-16 w-auto mx-auto dark:hidden"
                />
                <img 
                  src="/logo-dark.svg" 
                  alt="YaDela" 
                  className="h-16 w-auto mx-auto hidden dark:block"
                />
              </div>
              {forgotSent ? (
                <>
                  <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Письмо отправлено</CardTitle>
                  <CardDescription>
                    Проверьте почту {forgotEmail}
                  </CardDescription>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Mail className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="text-2xl font-bold">Забыли пароль?</CardTitle>
                  <CardDescription>
                    Введите email, и мы отправим ссылку для сброса
                  </CardDescription>
                </>
              )}
            </CardHeader>
            <CardContent>
              {forgotSent ? (
                <div className="space-y-4">
                  <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-300">
                    <p className="mb-1 font-medium">Если аккаунт с таким email существует, вы получите письмо с инструкциями.</p>
                    <p className="text-emerald-600 dark:text-emerald-400">Проверьте также папку «Спам».</p>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Вернуться к входу
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="email@example.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="pl-10"
                        required
                        autoComplete="email"
                        autoFocus
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={forgotLoading} data-testid="forgot-submit-btn">
                    {forgotLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Отправить ссылку
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full text-muted-foreground"
                    onClick={() => setShowForgotPassword(false)}
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Вернуться к входу
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left side - Features */}
      <AuthLeftPanel />

      {/* Right side - Auth forms */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-border shadow-sm">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden mb-3 mt-2">
              <img 
                src="/logo-light.svg" 
                alt="YaDela" 
                className="h-16 w-auto mx-auto dark:hidden"
              />
              <img 
                src="/logo-dark.svg" 
                alt="YaDela" 
                className="h-16 w-auto mx-auto hidden dark:block"
              />
            </div>
            <CardTitle className="text-2xl font-bold">
              {activeTab === 'login' ? 'Вход в систему' : 'Регистрация'}
            </CardTitle>
            <CardDescription>
              {activeTab === 'login' 
                ? 'Войдите в свой аккаунт' 
                : 'Создайте новый аккаунт'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={(tab) => { setActiveTab(tab); setLoginError(null); }}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Вход</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Регистрация</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="email@example.com"
                        value={loginEmail}
                        onChange={(e) => { setLoginEmail(e.target.value); setLoginError(null); }}
                        className={`pl-10 ${loginError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        required
                        autoComplete="email"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Пароль</Label>
                      <button
                        type="button"
                        className="text-xs text-primary hover:text-primary/80 hover:underline font-medium transition-colors"
                        onClick={() => setShowForgotPassword(true)}
                        data-testid="forgot-password-link"
                      >
                        Забыли пароль?
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => { setLoginPassword(e.target.value); setLoginError(null); }}
                        className={`pl-10 ${loginError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        required
                        autoComplete="current-password"
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  {/* Inline error block */}
                  {loginError && (
                    <div
                      className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                      data-testid="login-error-message"
                      role="alert"
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{loginError}</span>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Войти
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Имя</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Ваше имя"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="pl-10"
                        required
                        autoComplete="name"
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="email@example.com"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="pl-10"
                        required
                        autoComplete="email"
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Пароль</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Минимум 8 символов"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="pl-10"
                        minLength={8}
                        required
                        autoComplete="new-password"
                        data-testid="register-password-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Минимум 8 символов</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">Подтвердите пароль</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-confirm-password"
                        type="password"
                        placeholder="Повторите пароль"
                        value={registerConfirmPassword}
                        onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                        className="pl-10"
                        minLength={8}
                        required
                        autoComplete="new-password"
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                  </div>

                  {/* Captcha */}
                  {captchaEnabled && (
                    <div className="space-y-2">
                      <Label htmlFor="captcha-answer">
                        Решите пример: <span className="font-bold text-foreground">{captchaQuestion} = ?</span>
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="captcha-answer"
                          type="number"
                          placeholder="Ваш ответ"
                          value={captchaAnswer}
                          onChange={(e) => setCaptchaAnswer(e.target.value)}
                          required
                          className="flex-1"
                          data-testid="captcha-answer-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={loadCaptcha}
                          disabled={captchaLoading}
                          title="Обновить капчу"
                          className="shrink-0"
                        >
                          <RefreshCw className={`h-4 w-4 ${captchaLoading ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Inline error block for register */}
                  {registerError && (
                    <div
                      className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                      data-testid="register-error-message"
                      role="alert"
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>{registerError}</span>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Создать аккаунт
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">или</span>
              </div>
            </div>

            {/* OAuth Buttons */}
            <div className="flex flex-col gap-3">
              {/* Yandex OAuth */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 relative font-medium"
                onClick={handleYandexLogin}
                disabled={loading || yandexLoading || googleLoading || vkLoading}
                data-testid="yandex-auth-btn"
              >
                {yandexLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#FC3F1D"/>
                    <path d="M13.32 7.666H12.48C10.855 7.666 10.012 8.534 10.012 9.754C10.012 11.144 10.601 11.84 11.816 12.647L12.826 13.31L10.07 17.874H7.686L10.164 13.776C8.691 12.733 7.868 11.738 7.868 9.87C7.868 7.491 9.502 5.834 12.472 5.834H15.534V17.874H13.32V7.666Z" fill="white"/>
                  </svg>
                )}
                Войти через Яндекс
              </Button>

              {/* Google OAuth */}
              {GOOGLE_CLIENT_ID && (
                <GoogleLoginButton 
                  onLogin={handleGoogleLogin} 
                  disabled={loading || yandexLoading || googleLoading || vkLoading}
                  googleLoading={googleLoading}
                />
              )}

              {/* VK OAuth */}
              <Button
                type="button"
                variant="outline"
                className="w-full h-11 relative font-medium"
                onClick={handleVkLogin}
                disabled={loading || yandexLoading || googleLoading || vkLoading}
                data-testid="vk-auth-btn"
              >
                {vkLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z" fill="#0077FF"/>
                    <path d="M12.77 16.98H13.58C13.58 16.98 13.82 16.95 13.94 16.82C14.05 16.7 14.05 16.48 14.05 16.48C14.05 16.48 14.03 15.39 14.54 15.23C15.04 15.07 15.69 16.29 16.38 16.75C16.9 17.09 17.3 17.02 17.3 17.02L19.07 16.98C19.07 16.98 19.99 16.92 19.56 16.21C19.52 16.15 19.29 15.66 18.15 14.6C16.96 13.5 17.12 13.68 18.57 11.75C19.46 10.56 19.82 9.82 19.69 9.47C19.57 9.13 18.82 9.22 18.82 9.22L16.82 9.23C16.82 9.23 16.67 9.21 16.56 9.28C16.45 9.35 16.38 9.5 16.38 9.5C16.38 9.5 16.03 10.39 15.56 11.15C14.58 12.73 14.18 12.82 14.02 12.71C13.65 12.47 13.74 11.73 13.74 11.2C13.74 9.61 13.98 8.95 13.27 8.78C13.04 8.72 12.87 8.69 12.31 8.68C11.6 8.67 11 8.69 10.66 8.85C10.43 8.96 10.25 9.21 10.35 9.22C10.48 9.24 10.77 9.3 10.92 9.51C11.12 9.78 11.11 10.39 11.11 10.39C11.11 10.39 11.24 12.49 10.81 12.72C10.52 12.88 10.12 12.55 9.23 11.13C8.78 10.39 8.44 9.57 8.44 9.57C8.44 9.57 8.38 9.42 8.27 9.35C8.14 9.26 7.96 9.23 7.96 9.23L6.07 9.24C6.07 9.24 5.79 9.25 5.68 9.37C5.59 9.47 5.67 9.69 5.67 9.69C5.67 9.69 7.28 13.48 9.11 15.4C10.78 17.15 12.77 16.98 12.77 16.98Z" fill="white"/>
                  </svg>
                )}
                Войти через VK
              </Button>
            </div>

            {/* Terms notice */}
            <p className="text-[11px] text-muted-foreground text-center mt-5 leading-relaxed">
              Входя или регистрируясь, вы соглашаетесь с{' '}
              <button 
                type="button"
                onClick={() => setShowTerms(true)}
                className="text-orange-500 hover:text-orange-600 underline underline-offset-2"
              >
                Пользовательским соглашением и Политикой конфиденциальности
              </button>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Terms Dialog */}
      {showTerms && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowTerms(false)} />
          <div className="relative bg-background border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <h2 className="text-lg font-semibold">Пользовательское соглашение</h2>
              <button 
                onClick={() => setShowTerms(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-5 text-sm text-foreground/90 leading-relaxed space-y-5">
              <p className="text-xs text-muted-foreground">Последнее обновление: 8 марта 2025 г.</p>
              
              <div>
                <h3 className="font-semibold mb-2">1. Общие положения</h3>
                <p>Настоящее Пользовательское соглашение регулирует отношения между администрацией сервиса <strong>YaDela</strong> (далее — «Сервис») и лицом (далее — «Пользователь»), использующим Сервис. Используя Сервис, вы подтверждаете, что принимаете все условия настоящего Соглашения.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">2. Описание Сервиса</h3>
                <p>YaDela — CRM-система для малого бизнеса и частных мастеров: управление заказами, клиентами, задачами, финансами, конструктор форм, календарь и аналитика.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">3. Условия «как есть» (AS IS)</h3>
                <p>Сервис предоставляется на условиях <strong>«как есть»</strong>. Администрация:</p>
                <ul className="list-disc list-inside mt-1 space-y-1 text-foreground/80">
                  <li>Не гарантирует бесперебойную и безошибочную работу</li>
                  <li>Не несёт ответственности за убытки, связанные с использованием Сервиса</li>
                  <li>Не гарантирует сохранность данных при технических сбоях</li>
                  <li>Вправе изменять функциональность без уведомления</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">4. Учётная запись</h3>
                <ul className="list-disc list-inside space-y-1 text-foreground/80">
                  <li>Для работы необходима регистрация с действительным email</li>
                  <li>Вы отвечаете за сохранность учётных данных</li>
                  <li>Запрещена передача доступа третьим лицам</li>
                  <li>Аккаунт может быть заблокирован при нарушении условий</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">5. Тарифы и оплата</h3>
                <p>Бесплатный тариф «Демо» с ограничениями и платный «Про» с полным функционалом. Цены могут изменяться. Возврат средств — по законодательству РФ.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">6. Персональные данные</h3>
                <p>Мы собираем: имя, email, данные OAuth-профилей, данные вводимые в Сервис (заказы, клиенты и др.), технические данные (IP, браузер). Данные используются для работы Сервиса, авторизации, уведомлений и поддержки.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">7. Защита и передача данных</h3>
                <p>Пароли хранятся в хешированном виде, передача по HTTPS. Мы не продаём данные третьим лицам. Передача возможна только для работы интеграций (Telegram, email), по закону РФ или с вашего согласия.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">8. Ваши права</h3>
                <ul className="list-disc list-inside space-y-1 text-foreground/80">
                  <li>Запросить информацию о своих данных</li>
                  <li>Потребовать исправления или удаления данных</li>
                  <li>Удалить аккаунт через поддержку</li>
                  <li>Отозвать согласие на обработку</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">9. Cookies</h3>
                <p>Сервис использует cookie и localStorage для авторизации, сохранения настроек и корректной работы.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">10. Ограничение ответственности</h3>
                <p>Пользователь использует Сервис на свой страх и риск. Администрация не несёт ответственности за любые убытки, потерю данных, временную недоступность или действия третьих лиц.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">11. Изменения</h3>
                <p>Администрация вправе изменять Соглашение. Продолжение использования означает согласие с новой редакцией.</p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">12. Контакты</h3>
                <p>По вопросам обращайтесь через раздел «Поддержка» в Сервисе.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border shrink-0">
              <button
                onClick={() => setShowTerms(false)}
                className="w-full py-2.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-xl transition-colors"
              >
                Понятно
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Google Login Button component (uses useGoogleLogin hook for custom styling)
function GoogleLoginButton({ onLogin, disabled, googleLoading }) {
  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      onLogin(tokenResponse.access_token);
    },
    onError: () => {
      toast.error('Ошибка авторизации через Google');
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full h-11 relative font-medium"
      onClick={() => googleLogin()}
      disabled={disabled}
      data-testid="google-auth-btn"
    >
      {googleLoading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      )}
      Войти через Google
    </Button>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, register, googleLogin, yandexLogin, vkLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [yandexLoading, setYandexLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [vkLoading, setVkLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState(null);

  // Register form
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerError, setRegisterError] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Forgot password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Captcha
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [captchaId, setCaptchaId] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  // Load captcha status and generate if enabled
  useEffect(() => {
    captchaAPI.status().then(res => {
      const enabled = res.data.enabled;
      setCaptchaEnabled(enabled);
      if (enabled) {
        loadCaptcha();
      }
    }).catch(() => {});
  }, []);

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer('');
    try {
      const res = await captchaAPI.generate();
      setCaptchaId(res.data.captcha_id);
      setCaptchaQuestion(res.data.question);
    } catch {
      toast.error('Ошибка загрузки капчи');
    } finally {
      setCaptchaLoading(false);
    }
  };

  // Handle Yandex OAuth callback
  useEffect(() => {
    const yandexToken = searchParams.get('yandex_token');
    const yandexError = searchParams.get('yandex_error');
    
    if (yandexToken) {
      // Clear the URL params
      setSearchParams({}, { replace: true });
      setYandexLoading(true);
      
      yandexLogin(yandexToken)
        .then(() => {
          toast.success('Добро пожаловать!');
          navigate('/');
        })
        .catch((err) => {
          console.error('Yandex login error:', err);
          toast.error('Ошибка авторизации через Яндекс');
        })
        .finally(() => {
          setYandexLoading(false);
        });
    }
    
    if (yandexError) {
      setSearchParams({}, { replace: true });
      toast.error('Ошибка авторизации через Яндекс');
    }

    // Handle VK OAuth callback
    const vkToken = searchParams.get('vk_token');
    const vkError = searchParams.get('vk_error');
    
    if (vkToken) {
      setSearchParams({}, { replace: true });
      setVkLoading(true);
      
      vkLogin(vkToken)
        .then(() => {
          toast.success('Добро пожаловать!');
          navigate('/');
        })
        .catch((err) => {
          console.error('VK login error:', err);
          toast.error('Ошибка авторизации через VK');
        })
        .finally(() => {
          setVkLoading(false);
        });
    }
    
    if (vkError) {
      setSearchParams({}, { replace: true });
      toast.error('Ошибка авторизации через VK');
    }
  }, [searchParams, setSearchParams, yandexLogin, vkLogin, navigate]);

  const handleYandexLogin = () => {
    window.location.href = `${window.location.origin}/api/auth/yandex/login`;
  };

  const handleVkLogin = () => {
    window.location.href = `${window.location.origin}/api/auth/vk/login`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Добро пожаловать!');
      navigate('/');
    } catch (error) {
      const message = getErrorMessage(error) || 'Ошибка входа. Попробуйте ещё раз.';
      setLoginError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRegisterError(null);
    if (!registerName.trim() || registerName.trim().length > 100) {
      setRegisterError(registerName.trim() ? 'Имя слишком длинное (максимум 100 символов)' : 'Введите имя');
      setLoading(false);
      return;
    }
    if (registerPassword.length < 8) {
      setRegisterError('Пароль должен содержать минимум 8 символов');
      setLoading(false);
      return;
    }
    if (registerPassword.length > 128) {
      setRegisterError('Пароль слишком длинный (максимум 128 символов)');
      setLoading(false);
      return;
    }
    if (registerPassword !== registerConfirmPassword) {
      setRegisterError('Пароли не совпадают');
      setLoading(false);
      return;
    }
    if (captchaEnabled && !captchaAnswer.trim()) {
      setRegisterError('Введите ответ на капчу');
      setLoading(false);
      return;
    }
    try {
      const captchaData = captchaEnabled ? { captcha_id: captchaId, captcha_answer: parseInt(captchaAnswer, 10) } : {};
      await register(registerName, registerEmail, registerPassword, captchaData);
      toast.success('Регистрация успешна!');
      navigate('/');
    } catch (error) {
      const message = getErrorMessage(error) || 'Ошибка регистрации. Попробуйте ещё раз.';
      setRegisterError(message);
      // Reload captcha on error
      if (captchaEnabled) {
        loadCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (error) {
      toast.error('Ошибка отправки. Попробуйте ещё раз.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleGoogleLogin = async (accessToken) => {
    setGoogleLoading(true);
    try {
      await googleLogin(accessToken);
      toast.success('Добро пожаловать!');
      navigate('/');
    } catch (error) {
      toast.error('Ошибка авторизации через Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  const contentProps = {
    activeTab,
    setActiveTab,
    loginEmail,
    setLoginEmail,
    loginPassword,
    setLoginPassword,
    loginError,
    setLoginError,
    registerName,
    setRegisterName,
    registerEmail,
    setRegisterEmail,
    registerPassword,
    setRegisterPassword,
    registerConfirmPassword,
    setRegisterConfirmPassword,
    registerError,
    agreeTerms,
    setAgreeTerms,
    showTerms,
    setShowTerms,
    loading,
    handleLogin,
    handleRegister,
    handleGoogleLogin,
    googleLoading,
    handleYandexLogin,
    yandexLoading,
    handleVkLogin,
    vkLoading,
    showForgotPassword,
    setShowForgotPassword,
    forgotEmail,
    setForgotEmail,
    forgotLoading,
    forgotSent,
    handleForgotPassword,
    captchaEnabled,
    captchaQuestion,
    captchaAnswer,
    setCaptchaAnswer,
    captchaLoading,
    loadCaptcha
  };

  // Wrap with GoogleOAuthProvider only if client ID exists
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthContent {...contentProps} />
      </GoogleOAuthProvider>
    );
  }

  return <AuthContent {...contentProps} />;
}
